/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2024 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ddMMyyyy } from "../../constant";
import { BigText } from "../Displays/BigText";
import { formatValidDate } from "../../helpers";
import { IStudyOrderDetailsResponse } from "../../types/patientPortal";
import { calculateRemainingMinutes } from "../../utils/date";
import InfoIcon from "@advapacs/common/src/assets/icons/ic-information.svg";
import ClockIcon from "@advapacs/common/src/assets/icons/ic-clock.svg";
import WarningNotAvailableIcon from "@advapacs/common/src/assets/icons/warning-icon-not-available.svg";

import { convertUnitTImeMinutesToString } from "../../utils/format";

interface IReportCard {
  item?: IStudyOrderDetailsResponse;
  dateFormat?: string;
  plainDateFormat?: string;
  isMobile?: boolean;
  isMobileOnly?: boolean;
  preview?: () => void;
  download?: () => void;
}

export const ReportCard: FC<IReportCard> = ({
  item,
  dateFormat = ddMMyyyy,
  plainDateFormat = ddMMyyyy,
  isMobile,
  isMobileOnly,
}) => {
  const { t } = useTranslation();
  const [imagesAvailableAt, setImagesAvailableAt] = useState(0);
  const [reportAvailableAt, setReportAvailableAt] = useState(0);
  const mobileLayout = useMemo(() => {
    return isMobile || isMobileOnly;
  }, [isMobile, isMobileOnly]);

  const modalityValue = useMemo(() => {
    if (!item) return "";

    return item?.modalities?.join(", ");
  }, [item]);

  const examDateValue = useMemo(() => {
    if (!item) return "";
    return formatValidDate(item?.date, dateFormat);
  }, [item, dateFormat]);

  const reporter = useMemo(() => {
    if (!item) return;
    return item.reportedBy || "";
  }, [item]);

  const referrerValue = useMemo(() => {
    if (!item) return;
    return item.referrer || "";
  }, [item]);

  const imageUntilMinutes = calculateRemainingMinutes(
    item?.imagesAvailableUntil
  );
  const reportUntilMinutes = calculateRemainingMinutes(
    item?.reportAvailableUntil
  );

  useEffect(() => {
    const imageMinutes = calculateRemainingMinutes(item?.imagesAvailableAt);
    const reportMinutes = calculateRemainingMinutes(item?.reportAvailableAt);
    setImagesAvailableAt(imageMinutes);
    setReportAvailableAt(reportMinutes);
    let imageInterval: NodeJS.Timeout;
    let reportInterval: NodeJS.Timeout;
    if (imageMinutes > 0) {
      imageInterval = setInterval(() => {
        setImagesAvailableAt((prev) => {
          const newMinutes = prev - 1;
          if (newMinutes > 0) return newMinutes;
          clearInterval(imageInterval);
          return 0;
        });
      }, 60000);
    }
    if (reportMinutes > 0) {
      reportInterval = setInterval(() => {
        setReportAvailableAt((prev) => {
          const newMinutes = prev - 1;
          if (newMinutes > 0) return newMinutes;
          clearInterval(reportInterval);
          return 0;
        });
      }, 60000);
    }
    // Clear intervals when component unmounts or item changes
    return () => {
      clearInterval(imageInterval);
      clearInterval(reportInterval);
    };
  }, [item]);

  const getImageInfoMessage = useMemo(() => {
    if (imagesAvailableAt > 0) {
      return (
        <div className="available-studies bg-available p-xs b-r-sm d-flex align-items-center m-b-xxs">
          <img src={InfoIcon} className="m-r-xxs" alt="Info Icon" />
          <span>
            Images will be available in{" "}
            {convertUnitTImeMinutesToString(imagesAvailableAt)}
          </span>
        </div>
      );
    }

    if (item?.hasImages && imageUntilMinutes != 0) {
      if (imageUntilMinutes > 0) {
        return (
          <div className="alert-info p-xs b-r-sm d-flex align-items-center m-b-xxs">
            <img src={ClockIcon} className="m-r-xxs" alt="Info Icon" />
            <span>
              Images are available until{" "}
              {formatValidDate(item?.imagesAvailableUntil, plainDateFormat)}
            </span>
          </div>
        );
      } else {
        return (
          <div className="alert bg-light p-xs b-r-sm d-flex align-items-center m-b-xxs">
            <img
              src={WarningNotAvailableIcon}
              className="m-r-xxs"
              alt="Warn Icon"
            />
            <span>
              Images were available until{" "}
              {formatValidDate(item?.imagesAvailableUntil, plainDateFormat)}
            </span>
          </div>
        );
      }
    }
    return null;
  }, [imageUntilMinutes, imagesAvailableAt, item, plainDateFormat]);

  const getReportInfoMessage = useMemo(() => {
    if (reportAvailableAt > 0) {
      return (
        <div className="available-studies bg-available p-xs b-r-sm d-flex align-items-center m-b-xxs">
          <img src={InfoIcon} className="m-r-xxs" alt="Info Icon" />
          <span>
            Report will be available in{" "}
            {convertUnitTImeMinutesToString(reportAvailableAt)}
          </span>
        </div>
      );
    }
    if (item?.hasReport && reportUntilMinutes != 0) {
      if (reportUntilMinutes > 0) {
        return (
          <div className="alert-info p-xs b-r-sm d-flex align-items-center m-b-xxs">
            <img src={ClockIcon} className="m-r-xxs" alt="Info Icon" />
            <span>
              Report is available until{" "}
              {formatValidDate(item?.reportAvailableUntil, plainDateFormat)}
            </span>
          </div>
        );
      } else {
        return (
          <div className="alert bg-light p-xs b-r-sm d-flex align-items-center m-b-xxs">
            <img
              src={WarningNotAvailableIcon}
              className="m-r-xxs"
              alt="Warn Icon"
            />
            <span>
              Report was available until{" "}
              {formatValidDate(item?.reportAvailableUntil, plainDateFormat)}
            </span>
          </div>
        );
      }
    }
    return null;
  }, [reportAvailableAt]);

  return (
    <>
      <h5 className="font-weight m-b-xs">{item?.description}</h5>

      <div className="details">
        {getImageInfoMessage} {getReportInfoMessage}
        <div className="study-details d-flex  w-100">
          <div className="study-info d-flex flex-1 w-100">
            <div className={`left flex-1 ${!mobileLayout ? "m-r-md" : ""}`}>
              <div className="d-flex date m-b-xxs w-100">
                <span className="text-label w-150">Date</span>
                <BigText maxWidth={"100%"} text={examDateValue} />
              </div>

              <div className="d-flex date m-b-xxs w-100">
                <span className="text-label w-150">Reported At</span>
                <BigText
                  maxWidth={"100%"}
                  text={formatValidDate(item?.verifiedAt, dateFormat)}
                />
              </div>

              {mobileLayout && (
                <>
                  <div className={`d-flex date m-b-xxs w-100 `}>
                    <span className="text-label w-150">
                      {t("accession_number")}
                    </span>
                    <BigText maxWidth={"100%"} text={item?.accessionNumber} />
                  </div>

                  <div className={`d-flex date m-b-xxs w-100 `}>
                    <span className="text-label w-150">Modality</span>
                    <BigText maxWidth={"100%"} text={modalityValue} />
                  </div>
                  <div className={`d-flex date m-b-xxs w-100 `}>
                    <span className="text-label w-150">{t("referrer")}</span>
                    <span>{referrerValue} </span>
                  </div>

                  <div className={`d-flex date m-b-xxs w-100 `}>
                    <span className="text-label w-150">Reported By</span>
                    <span>{reporter}</span>
                  </div>
                </>
              )}
            </div>

            {!mobileLayout && (
              <>
                <div
                  className={`center flex-1 ${!mobileLayout ? "m-r-md" : ""}`}
                >
                  <div className={`d-flex date m-b-xxs w-100 `}>
                    <span className="text-label w-150">
                      {t("accession_number")}
                    </span>
                    <BigText maxWidth={"100%"} text={item?.accessionNumber} />
                  </div>

                  <div className={`d-flex date m-b-xxs w-100 `}>
                    <span className="text-label w-150">Modality</span>
                    <BigText maxWidth={"100%"} text={modalityValue} />
                  </div>
                </div>
                <div className="right flex-1">
                  <div className="d-flex w-100  m-b-xxs">
                    <span className="text-label w-150">{t("referrer")}</span>
                    <span>{referrerValue} </span>
                  </div>

                  <div className={`d-flex date m-b-xxs w-100 `}>
                    <span className="text-label w-150">Reported By</span>
                    <span>{reporter}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
